import { useEffect, useState } from "react"
import { useMaterialUIController } from "context"

// @mui material components
import Grid from "@mui/material/Grid"
import CircularProgress from "@mui/material/CircularProgress"
import Card from "@mui/material/Card"
import Divider from "@mui/material/Divider"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Collapse from "@mui/material/Collapse"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import Icon from "@mui/material/Icon"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDPagination from "components/MDPagination"
import MDTypography from "components/MDTypography"

// Material Dashboard 2 React examples
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"

// Organizaciones page components
import OrganizacionesInformation from "layouts/asistencia/dependencias/components/OrganizacionesInformation"

// React Router
import { Link, useNavigate } from "react-router-dom"

// Config and JWT
import config from "config"
import { jwtDecode } from "jwt-decode"

// Services
import { fetchDependencias, fetchSucursales } from "layouts/asistencia/dependencias/components/add/service"

function Organizaciones() {
  const [controller] = useMaterialUIController()
  const { darkMode } = controller

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const navigate = useNavigate()

  // Data states
  const [dependencias, setDependencias] = useState([])
  const [sucursales, setSucursales] = useState([])

  // Loading and pagination
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)

  // Filters
  const [selectedSucursal, setSelectedSucursal] = useState("")
  const [busquedaText, setBusquedaText] = useState("")

  // Verify token
  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  // Load initial data
  useEffect(() => {
    loadSucursales()
    loadDependencias(0)
  }, [])

  const loadSucursales = async () => {
    try {
      const data = await fetchSucursales()
      setSucursales(data || [])
    } catch (error) {
      console.error("Error loading sucursales:", error)
    }
  }

  const loadDependencias = async (page) => {
    setLoading(true)
    try {
      const params = {}

      if (selectedSucursal) {
        params.sucursalId = selectedSucursal
      }

      if (busquedaText) {
        params.nombre = busquedaText
      }

      const result = await fetchDependencias(params)

      if (Array.isArray(result)) {
        setDependencias(result)
        setTotalPages(1)
        setTotalElements(result.length)
      } else {
        setDependencias([])
        setTotalPages(0)
        setTotalElements(0)
      }
    } catch (error) {
      console.error("Error loading dependencias:", error)
      setDependencias([])
    } finally {
      setLoading(false)
    }
  }

  const gotoPage = (p) => {
    setPageIndex(p)
  }

  const reloadDependencias = () => {
    loadDependencias(pageIndex)
  }

  const handleBuscar = () => {
    setPageIndex(0)
    loadDependencias(0)
  }

  const handleClearFilters = () => {
    setSelectedSucursal("")
    setBusquedaText("")
    setPageIndex(0)
    setDependencias([])
  }

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      handleBuscar()
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={8}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              {/* Filtros */}
              <Card>
                <MDBox px={2}>
                  <MDBox display="flex" alignItems="center" py={2} px={2}>
                    <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                      Filtros
                    </MDTypography>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      onClick={handleExpandClick}
                      sx={{ cursor: "pointer", ml: 1 }}
                    >
                      {expanded ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                    </MDBox>
                  </MDBox>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} pb={2}>
                      {/* Sucursal */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Sucursal</InputLabel>
                          <Select
                            label="Sucursal"
                            variant="outlined"
                            fullWidth
                            value={selectedSucursal}
                            onChange={(e) => setSelectedSucursal(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Ninguna</MenuItem>
                            {sucursales.map((suc) => (
                              <MenuItem key={suc.idSucursal} value={suc.idSucursal}>
                                {suc.descripcion}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Búsqueda */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Buscar por nombre"
                          variant="outlined"
                          fullWidth
                          value={busquedaText}
                          onChange={(e) => setBusquedaText(e.target.value)}
                          onKeyDown={handleKeyDown}
                        />
                      </Grid>

                      {/* Botones */}
                      <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                          <MDBox>
                            <MDButton type="submit" variant="gradient" color="info" onClick={handleBuscar}>
                              Buscar
                            </MDButton>
                            &nbsp;
                            <MDButton type="button" variant="outlined" color="info" onClick={handleClearFilters}>
                              Limpiar
                            </MDButton>
                          </MDBox>

                          <Link to="/asistencia/dependencias/agregar" style={{ textDecoration: "none" }}>
                            <MDButton type="button" variant="gradient" color="success">
                              Agregar
                            </MDButton>
                          </Link>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Collapse>
                </MDBox>
              </Card>

              <Divider />

              {/* Tabla de Organizaciones */}
              <Card pt={5} mt={3}>
                {/* Contenido */}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : dependencias && dependencias.length > 0 ? (
                  <OrganizacionesInformation organizaciones={dependencias} onReload={reloadDependencias} />
                ) : (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <MDTypography variant="h6" color="text">
                      No hay datos para mostrar
                    </MDTypography>
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default Organizaciones
